import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

const quizQuestions = [
  {
    question: "What is a key aspect of global environmental responsibility?",
    options: [
      "Considering the worldwide impact of personal choices",
      "Ignoring international environmental issues",
      "Focusing only on economic benefits"
    ],
    correctAnswer: 0,
    explanation: "Global environmental responsibility includes considering the worldwide impact of personal choices, recognizing that local actions contribute to global environmental challenges and solutions."
  },
  {
    question: "What does an ecological footprint measure?",
    options: [
      "The size of a person's carbon emissions only",
      "The total area of land and water needed to support a person's lifestyle",
      "Only the waste a person produces"
    ],
    correctAnswer: 1,
    explanation: "An ecological footprint measures the biologically productive land and water area required to produce all the resources a person consumes and to absorb the waste they generate, using prevailing technology."
  },
  {
    question: "What is the main goal of a circular economy?",
    options: [
      "To eliminate waste and pollution",
      "To maximize resource extraction",
      "To increase consumption rates"
    ],
    correctAnswer: 0,
    explanation: "A circular economy aims to eliminate waste and the continual use of resources by designing out waste, keeping products and materials in use, and regenerating natural systems."
  },
  {
    question: "How are climate change and biodiversity loss connected?",
    options: [
      "They are completely unrelated",
      "Climate change exacerbates biodiversity loss through habitat destruction and species migration challenges",
      "Biodiversity loss causes climate change"
    ],
    correctAnswer: 1,
    explanation: "Climate change and biodiversity loss are interconnected - rising temperatures, changing precipitation patterns, and extreme weather events destroy habitats and make it difficult for species to adapt or migrate."
  },
  {
    question: "What role can individuals play in global sustainability?",
    options: [
      "No significant role",
      "Making sustainable choices and advocating for systemic change",
      "Only through large donations to environmental organizations"
    ],
    correctAnswer: 1,
    explanation: "Individuals can contribute to global sustainability by making conscious consumption choices, reducing waste, supporting sustainable businesses, and advocating for policy changes that address environmental challenges."
  }
];

const EnvironmentalModule5QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('environmentalM5');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(responses[prevQuestion] || null);
      setShowExplanation(responses[prevQuestion] !== undefined);
    }
  };

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          Congratulations!
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 5 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              Key Achievements
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                Sustainable living principles
              </li>
              <li>
                Ecological footprint concepts
              </li>
              <li>
                Circular economy thinking
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              Next Steps
            </h3>
            <p>Continue your environmental wellness journey by learning how to integrate environmental well-being into holistic life balance!</p>
            <p>Ready to see how environmental wellness connects with all aspects of well-being?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/environmental-resources')}
          >
            Return to Environmental Resources
          </button>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left environmental-panel">
          <div className="module-header">
            <h1>Module 5 Quiz</h1>
            <p className="module-subtitle">Sustainability and Global Responsibility</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about sustainable living and global environmental responsibility.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/environmental-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalModule5QuizPage;

